import React, { useRef, useState } from "react";
import { FaCheck, FaUpload, FaFileExcel } from "react-icons/fa";
import * as XLSX from "xlsx";
import {
  postFinalizedData,
  uploadFinalizedUniversities,
} from "../../services/admin.services";
import UniversityTable from "./UniversityTable";
import AdminComments from "../common/AdminComments";
import EditableNotes from "./EditableNotes";
import toast from "react-hot-toast";

const MONTHS_MAP = {
  jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2,
  apr: 3, april: 3, may: 4, jun: 5, june: 5, jul: 6, july: 6,
  aug: 7, august: 7, sep: 8, sept: 8, september: 8, oct: 9, october: 9,
  nov: 10, november: 10, dec: 11, december: 11,
};
const MONTHS_OUTPUT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const recoverExcelTimeDate = (d) => {
  if (!d || isNaN(d.getTime())) return d;
  const yr = d.getUTCFullYear();
  if (yr <= 1900) {
    const m = d.getUTCMonth();
    const dayOfMonth = d.getUTCDate();
    // Excel 1900 leap year bug days:
    // Jan: 31, Feb: 29 (60 total before March)
    const daysBeforeMonth = [0, 31, 60, 91, 121, 152, 182, 213, 244, 274, 305, 335];
    const totalDays = (daysBeforeMonth[m] || 0) + dayOfMonth;
    const hours = d.getUTCHours();
    const recYear = totalDays * 24 + hours;
    const recMonth = d.getUTCMinutes();
    const recDay = d.getUTCSeconds();
    if (recYear >= 2020 && recYear <= 2040 && recMonth >= 1 && recMonth <= 12 && recDay >= 1 && recDay <= 31) {
      return new Date(recYear, recMonth - 1, recDay);
    }
  }
  return d;
};

const formatDeadlineDate = (input) => {
  if (!input) return "";
  let d = null;
  if (input instanceof Date && !isNaN(input.getTime())) {
    d = recoverExcelTimeDate(input);
  } else {
    let str = String(input).trim();
    if (!str) return "";

    // Preserve non-date statuses / text
    if (/^(rolling|tbd|n\/?a|na|tentative|none|asap)$/i.test(str)) {
      return str;
    }

    // Recover dates corrupted by Excel duration/time interpretation: e.g. "24-03-1900 9.10.23 AM" or "1900-03-24..."
    if (str.includes("1900")) {
      const match1900 = str.match(/(\d{1,2})[-\/. ](\d{1,2})[-\/. ]1900[\s,]+(\d{1,2})[:.](\d{1,2})[:.](\d{1,2})/i) ||
                        str.match(/1900[-\/. ](\d{1,2})[-\/. ](\d{1,2})[T\s,]+(\d{1,2})[:.](\d{1,2})[:.](\d{1,2})/i);
      if (match1900) {
        let dVal, mVal, hr, min, sec;
        if (str.includes("1900-") || str.startsWith("1900")) {
          mVal = parseInt(match1900[1], 10);
          dVal = parseInt(match1900[2], 10);
        } else {
          dVal = parseInt(match1900[1], 10);
          mVal = parseInt(match1900[2], 10);
        }
        hr = parseInt(match1900[3], 10);
        min = parseInt(match1900[4], 10);
        sec = parseInt(match1900[5], 10);
        const daysBefore = [0, 31, 60, 91, 121, 152, 182, 213, 244, 274, 305, 335];
        const totDays = (daysBefore[mVal - 1] || 0) + dVal;
        const recY = totDays * 24 + hr;
        const recM = min;
        const recD = sec;
        if (recY >= 2020 && recY <= 2040 && recM >= 1 && recM <= 12 && recD >= 1 && recD <= 31) {
          d = new Date(recY, recM - 1, recD);
        }
      }
    }

    if (!d) {
      // 1. Excel serial number (e.g. 45678, 46387)
      const num = Number(str);
      if (!isNaN(num) && num > 30000 && num < 70000) {
        const epoch = new Date(Math.round((num - 25569) * 86400 * 1000));
        d = new Date(epoch.getUTCFullYear(), epoch.getUTCMonth(), epoch.getUTCDate());
      } else {
        // Clean up ordinal suffixes (15th -> 15, 1st -> 1, etc.)
        const cleaned = str.replace(/(\d{1,2})(st|nd|rd|th)\b/gi, "$1");

        // 2. ISO format: 2025.10.23, 2027-01-15, 2027/01/15, 2027.01.15
        const isoMatch = cleaned.match(/\b(\d{4})[-\/\. ](\d{1,2})[-\/\. ](\d{1,2})/);
        if (isoMatch) {
          d = new Date(parseInt(isoMatch[1], 10), parseInt(isoMatch[2], 10) - 1, parseInt(isoMatch[3], 10));
        } else {
          // 3. Day-first with month name: "15 Jan 2027", "15-Jan-2027", "15/Jan/2027", "15.Jan.2027", "15 January 2027"
          const dayFirst = cleaned.match(/\b(\d{1,2})[\s\-_\.\/]+([A-Za-z]{3,9})[\s\-_\.\/,]+(\d{2,4})\b/);
          if (dayFirst && MONTHS_MAP[dayFirst[2].toLowerCase()] !== undefined) {
            let yr = parseInt(dayFirst[3], 10);
            if (yr < 100) yr += 2000;
            d = new Date(yr, MONTHS_MAP[dayFirst[2].toLowerCase()], parseInt(dayFirst[1], 10));
          } else {
            // 4. Month-first with month name: "Jan 15 2027", "January 15, 2027", "Jan-15-2027"
            const monFirst = cleaned.match(/\b([A-Za-z]{3,9})[\s\-_\.\/]+(\d{1,2})[\s\-_\.\/,]+(\d{2,4})\b/);
            if (monFirst && MONTHS_MAP[monFirst[1].toLowerCase()] !== undefined) {
              let yr = parseInt(monFirst[3], 10);
              if (yr < 100) yr += 2000;
              d = new Date(yr, MONTHS_MAP[monFirst[1].toLowerCase()], parseInt(monFirst[2], 10));
            } else {
              // 5. Numeric: dd/mm/yyyy, dd-mm-yyyy, dd.mm.yyyy, mm/dd/yyyy
              const numMatch = cleaned.match(/\b(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2,4})\b/);
              if (numMatch) {
                const p1 = parseInt(numMatch[1], 10);
                const p2 = parseInt(numMatch[2], 10);
                let y = parseInt(numMatch[3], 10);
                if (y < 100) y += 2000;
                if (p1 > 12) d = new Date(y, p2 - 1, p1);
                else if (p2 > 12) d = new Date(y, p1 - 1, p2);
                else d = new Date(y, p2 - 1, p1); // default dd/mm/yyyy
              } else {
                // 6. Month + Year only (e.g. "January 2027" or "Jan 2027") -> defaults to 1st of month
                const monthYearMatch = cleaned.match(/\b([A-Za-z]{3,9})[\s\-_\.\/,]+(\d{4})\b/);
                if (monthYearMatch && MONTHS_MAP[monthYearMatch[1].toLowerCase()] !== undefined) {
                  d = new Date(parseInt(monthYearMatch[2], 10), MONTHS_MAP[monthYearMatch[1].toLowerCase()], 1);
                } else {
                  // 7. Day + Month only without year (e.g. "15 Jan", "15th January") -> current or next year
                  const dayMonthMatch = cleaned.match(/\b(\d{1,2})[\s\-_\.\/]+([A-Za-z]{3,9})\b/);
                  if (dayMonthMatch && MONTHS_MAP[dayMonthMatch[2].toLowerCase()] !== undefined) {
                    const now = new Date();
                    let yr = now.getFullYear();
                    const targetMonth = MONTHS_MAP[dayMonthMatch[2].toLowerCase()];
                    const targetDay = parseInt(dayMonthMatch[1], 10);
                    const candidate = new Date(yr, targetMonth, targetDay);
                    if (candidate < now) yr += 1;
                    d = new Date(yr, targetMonth, targetDay);
                  } else {
                    // Fallback: JS Date
                    const parsed = new Date(str);
                    if (!isNaN(parsed.getTime())) d = recoverExcelTimeDate(parsed);
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  if (!d || isNaN(d.getTime())) return String(input);
  const day = String(d.getDate()).padStart(2, "0");
  const mon = MONTHS_OUTPUT[d.getMonth()];
  const yr = d.getFullYear();
  return `${day} ${mon} ${yr}`;
};

const FinalizedUniversityTab = ({
  fetchStudentDetails,
  selectedStudent,
  finalizedCommentsRef,
  renderContent,
}) => {
  const [activeSection, setActiveSection] = useState("all"); // 'all', 'upload', 'table', 'notes', 'comments'
  const [file, setFile] = useState(null);
  const [jsonData, setJsonData] = useState(null);
  const [loading, setLoading] = useState(false);

  // File upload handlers
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      readExcelFile(selectedFile);
    }
  };

  function readExcelFile(file) {
    const reader = new FileReader();

    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);

      const workbook = XLSX.read(data, {
        type: "array",
        cellDates: true,
        cellStyles: true,
      });

      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

      if (rows.length > 1) {
        // Normalize headers
        const headers = rows[0].map((header) => {
          if (!header)
            return `column_${Math.random().toString(36).substr(2, 5)}`;

          const raw = String(header)
            .trim()
            .toLowerCase()
            .replace(/\s+/g, "")
            .replace(/\./g, "");

          if (
            [
              "srno",
              "srnumber",
              "srnumber.",
              "sr.no",
              "sr. no",
              "sr no",
            ].includes(raw)
          ) {
            return "sr.no.";
          }

          if (["loginid", "login_id", "login id", "log-inid"].includes(raw)) {
            return "loginid";
          }

          return String(header).trim().toLowerCase(); // fallback
        });

        const jsonData = rows
          .slice(1)
          .map((row) => {
            const obj = {};
            headers.forEach((header, index) => {
              let val = row[index] !== undefined ? row[index] : "";
              if (header && (header.includes("deadline") || header.includes("date")) && !header.includes("note")) {
                val = formatDeadlineDate(val);
              }
              obj[header] = val;
            });
            return obj;
          })
          .filter((row) => Object.values(row).some((value) => value !== ""));

        setJsonData({
          headers: headers,
          rows: jsonData,
        });
      } else {
        toast.error(
          "Excel file must contain headers and at least one data row"
        );
        setJsonData({ headers: [], rows: [] });
      }
    };

    reader.readAsArrayBuffer(file);
  }

  const handleCancel = () => {
    setFile(null);
    setJsonData(null);
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error("Please select a file before submitting.");
      return;
    }

    setLoading(true);

    try {
      if (!jsonData || jsonData.headers.length === 0) {
        throw new Error("No valid headers detected in the Excel file");
      }

      // Ensure all rows have a 'status' key - this is required by the API
      const processedRows = jsonData.rows.map((row) => {
        // If status field doesn't exist or is empty, set a default value
        if (!row.status || row.status.trim() === "") {
          return { ...row, status: "Pending" };
        }
        return row;
      });

      // Make sure all objects have the required 'status' field
      const statusHeaderExists = jsonData.headers.includes("status");

      // Store column order separately as per API requirements
      const columnOrder = [...jsonData.headers];
      if (!columnOrder.includes("status")) {
        columnOrder.push("status");
      }

      // Add status header if it doesn't exist
      if (!statusHeaderExists) {
        columnOrder.push("status");
      }

      // This fixes the API error by ensuring we send a proper array of objects
      // with each object containing a 'status' key
      const rowsWithStatus = jsonData.rows.map((row) => ({
        ...row,
        status: row.status?.toString().trim() || "Pending",
      }));

      // const finalizedData = {
      //   is_finalized_excel: rowsWithStatus, // ✅ exclude student_id to prevent nesting
      // };

      const metadataRow = {
        __columnOrder: columnOrder.map((col) => col.toLowerCase().trim()),
        status: "__meta__", // 👈 dummy status just to pass validation
      };

      // Final payload includes data + metadata
      const finalPayload = [...rowsWithStatus, metadataRow];
      // Upload to the server
      const id = selectedStudent?.student_id;
      const response = await postFinalizedData(id, finalPayload);

      if (response) {
        // Force refresh student details to get updated data
        await fetchStudentDetails();
        toast.success("Finalized universities uploaded successfully");

        setFile(null);
        setJsonData(null);
      }
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error(error.message || "Failed to upload. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Add column order visualization component
  const ColumnOrderPreview = ({ columns }) => {
    if (!columns || columns.length === 0) return null;

    return (
      <div className="mt-2">
        <strong className="text-sm text-gray-700">Column Order:</strong>
        <div className="flex flex-wrap items-center mt-1">
          {columns.map((col, index) => (
            <React.Fragment key={index}>
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-mono">
                {col}
              </span>
              {index < columns.length - 1 && (
                <span className="text-gray-400 mx-1">→</span>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  const downloadSampleExcel = () => {
    // Sample columns in a specific order
    const sampleData = [
      [
        "sr.no.",
        "status",
        "tag",
        "course",
        "university",
        "country",
        "applyLink",
        "deadline",
        "remarks",
      ],
      [
        "1",
        "Pending",
        "Safe",
        "Masters in Engineering",
        "MIT",
        "USA",
        "https://apply.mit.edu",
        "15 Dec 2025",
        "Top choice",
      ],
      [
        "2",
        "Submitted",
        "Ambitious",
        "Computer Science",
        "Stanford",
        "USA",
        "https://apply.stanford.edu",
        "10 Jan 2026",
        "Reach school",
      ],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sample");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "Sample_Finalized_Universities.xlsx");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Render the upload form section
  const renderUploadForm = () => (
    <div className="p-6">
      <div className="mb-4">
        <a
          onClick={downloadSampleExcel}
          className="text-blue-500 underline hover:text-blue-700 cursor-pointer text-sm flex items-center"
        >
          <FaFileExcel className="h-4 w-4 mr-1" />
          Download Sample Excel Format
        </a>
      </div>
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50 relative hover:border-purple-500 transition">
        <input
          type="file"
          accept=".xlsx, .xls"
          className="absolute inset-0 opacity-0 cursor-pointer"
          onChange={handleFileChange}
        />
        <div className="flex flex-col items-center justify-center py-4">
          <FaUpload className="h-10 w-10 text-gray-400 mb-2" />
          <p className="text-gray-500 text-center">
            {file ? file.name : "Click or drag file to upload"}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Supports Excel files (.xlsx, .xls)
          </p>
        </div>
      </div>

      {jsonData && (
        <div className="mt-4 bg-gray-100 p-4 rounded">
          <h3 className="text-gray-700 font-medium">File Preview:</h3>
          <p className="text-sm text-gray-600">
            {jsonData.headers.length} columns and {jsonData.rows.length} rows
            detected
          </p>

          {/* Status field warning if not found */}
          {!jsonData.headers.includes("status") && (
            <div className="mt-2 bg-yellow-50 border border-yellow-200 p-2 rounded text-sm text-yellow-800">
              <strong>Note:</strong> 'status' column not found. A default
              'Pending' status will be added to all rows.
            </div>
          )}

          {/* Enhanced column order visualization */}
          <ColumnOrderPreview columns={jsonData.headers} />

          {/* Show sample data if available */}
          {jsonData.rows.length > 0 && (
            <div className="mt-3 text-xs">
              <p className="text-gray-700 font-medium">First row preview:</p>
              <div className="bg-white p-2 rounded border mt-1 overflow-x-auto">
                <pre className="text-gray-600">
                  {JSON.stringify(jsonData.rows[0], null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end items-center mt-6 gap-3">
        <button
          onClick={handleCancel}
          className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          disabled={!file || loading}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center"
          disabled={!file || loading}
        >
          {loading ? (
            <>
              <svg
                className="animate-spin h-5 w-5 mr-2 border-t-2 border-white border-solid rounded-full"
                viewBox="0 0 24 24"
              ></svg>
              Uploading...
            </>
          ) : (
            "Upload Universities"
          )}
        </button>
      </div>
    </div>
  );

  // Render empty state for university table
  const renderEmptyUniversityTable = () => (
    <div className="py-12 text-center text-gray-500">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-12 w-12 mx-auto text-gray-400 mb-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
      <p>No finalized universities found.</p>
      <p className="mt-2 text-sm">
        Upload an Excel file or finalize universities from the shortlisting
        section.
      </p>
    </div>
  );

  return (
    <div className="p-4">
      {/* Section Navigation */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveSection("all")}
            className={`px-4 py-2 rounded-md transition ${
              activeSection === "all"
                ? "bg-purple-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            All Sections
          </button>
          <button
            onClick={() => setActiveSection("upload")}
            className={`px-4 py-2 rounded-md transition ${
              activeSection === "upload"
                ? "bg-purple-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Upload Universities
          </button>
          <button
            onClick={() => setActiveSection("table")}
            className={`px-4 py-2 rounded-md transition ${
              activeSection === "table"
                ? "bg-purple-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            University Table
          </button>
          <button
            onClick={() => setActiveSection("notes")}
            className={`px-4 py-2 rounded-md transition ${
              activeSection === "notes"
                ? "bg-purple-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            University Notes
          </button>
          <button
            onClick={() => setActiveSection("comments")}
            className={`px-4 py-2 rounded-md transition ${
              activeSection === "comments"
                ? "bg-purple-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Comments
          </button>
        </div>
      </div>

      {/* UPLOAD SECTION - FULL WIDTH WHEN EXPANDED */}
      {activeSection === "upload" && (
        <div className="mb-6">
          <div className="bg-purple-600 rounded-t-lg p-4 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-white flex items-center">
              <FaUpload className="mr-2" /> Upload Finalized Universities
            </h2>
            <button
              onClick={() => setActiveSection("all")}
              className="bg-white text-purple-600 px-3 py-1 rounded text-sm font-medium"
            >
              Back to All
            </button>
          </div>
          <div className="bg-white shadow-md rounded-b-lg">
            {renderUploadForm()}
          </div>
        </div>
      )}

      {/* ALL SECTIONS VIEW */}
      {activeSection === "all" && (
        <div>
          {/* Upload Universities Section */}
          <div className="mb-6">
            <div className="bg-purple-600 rounded-t-lg p-4 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-white flex items-center">
                <FaUpload className="mr-2" /> Upload Finalized Universities
              </h2>
              <button
                onClick={() => setActiveSection("upload")}
                className="bg-white text-purple-600 px-3 py-1 rounded text-sm font-medium"
              >
                Expand
              </button>
            </div>
            <div className="bg-white shadow-md rounded-b-lg p-4 overflow-auto max-h-96">
              {renderUploadForm()}
            </div>
          </div>

          {/* University Table Section */}
          <div className="mb-6">
            <div className="bg-purple-600 rounded-t-lg p-4 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-white flex items-center">
                <FaCheck className="mr-2" /> Finalized Universities
              </h2>
              <button
                onClick={() => setActiveSection("table")}
                className="bg-white text-purple-600 px-3 py-1 rounded text-sm font-medium"
              >
                Expand
              </button>
            </div>
            <div className="bg-white shadow-md rounded-b-lg p-4 overflow-auto max-1/2">
              {selectedStudent &&
              selectedStudent.is_finalized_excel &&
              selectedStudent.is_finalized_excel.length > 0 ? (
                <UniversityTable
                  activeTab={"finalizedUniversity"}
                  fetchStudentDetails={fetchStudentDetails}
                />
              ) : (
                renderEmptyUniversityTable()
              )}
            </div>
          </div>

          {/* Notes Section */}
          <div className="mb-6">
            <div className="bg-purple-600 rounded-t-lg p-4 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-white">
                University Notes
              </h2>
              <button
                onClick={() => setActiveSection("notes")}
                className="bg-white text-purple-600 px-3 py-1 rounded text-sm font-medium"
              >
                Expand
              </button>
            </div>
            <div className="bg-white shadow-md rounded-b-lg p-4 overflow-auto max-h-96">
              <EditableNotes studentId={selectedStudent?.student_id} />
            </div>
          </div>

          {/* Comments Section */}
          <div className="mb-6">
            <div className="bg-purple-600 rounded-t-lg p-4 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-white">
                Application Process Discussion
              </h2>
              <button
                onClick={() => setActiveSection("comments")}
                className="bg-white text-purple-600 px-3 py-1 rounded text-sm font-medium"
              >
                Expand
              </button>
            </div>
            <div
              ref={finalizedCommentsRef}
              className="bg-white shadow-md rounded-b-lg p-4 overflow-auto max-h-96"
            >
              {renderContent()}
            </div>
          </div>
        </div>
      )}

      {/* EXPANDED SECTIONS */}
      {activeSection === "table" && (
        <div className="mb-6">
          <div className="bg-purple-600 rounded-t-lg p-4 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-white flex items-center">
              <FaCheck className="mr-2" /> Finalized Universities
            </h2>
            <button
              onClick={() => setActiveSection("all")}
              className="bg-white text-purple-600 px-3 py-1 rounded text-sm font-medium"
            >
              Back to All
            </button>
          </div>
          <div className="bg-white shadow-md rounded-b-lg p-4">
            {selectedStudent &&
            selectedStudent.is_finalized_excel &&
            selectedStudent.is_finalized_excel.length > 0 ? (
              <UniversityTable
                activeTab={"finalizedUniversity"}
                fetchStudentDetails={fetchStudentDetails}
              />
            ) : (
              renderEmptyUniversityTable()
            )}
          </div>
        </div>
      )}

      {activeSection === "notes" && (
        <div className="mb-6">
          <div className="bg-purple-600 rounded-t-lg p-4 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-white">
              University Notes
            </h2>
            <button
              onClick={() => setActiveSection("all")}
              className="bg-white text-purple-600 px-3 py-1 rounded text-sm font-medium"
            >
              Back to All
            </button>
          </div>
          <div className="bg-white shadow-md rounded-b-lg p-4">
            <EditableNotes studentId={selectedStudent?.student_id} />
          </div>
        </div>
      )}

      {activeSection === "comments" && (
        <div className="mb-6">
          <div className="bg-purple-600 rounded-t-lg p-4 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-white">
              Application Process Discussion
            </h2>
            <button
              onClick={() => setActiveSection("all")}
              className="bg-white text-purple-600 px-3 py-1 rounded text-sm font-medium"
            >
              Back to All
            </button>
          </div>
          <div
            ref={finalizedCommentsRef}
            className="bg-white shadow-md rounded-b-lg p-4"
          >
            {renderContent()}
          </div>
        </div>
      )}
    </div>
  );
};

export default FinalizedUniversityTab;
